import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Navigate } from 'react-router-dom';
import { TOAST_AUTO_CLOSE_DELAY } from '../../constants/timing';
import AchievementCard from './AchievementCard';
import useAchievements from '../../hooks/useAchievements';
import achievementService from '../../services/achievementService';
import { getGamesConfig } from '../../config/games';
import './AchievementsPage.scss';

const AchievementsPage = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const { addToast } = useToast();
  const { handleNewAchievements } = useAchievements();
  const [achievements, setAchievements] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  // Define game types to be grouped under "Total Wins" - dynamically derived from config
  const gameTypes = getGamesConfig().map(game => game.id);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchAchievements();
    }
  }, [isAuthenticated, user]);

  const fetchAchievements = async () => {
    try {
      const data = await achievementService.getUserAchievements();
      setAchievements(data);
      
      // Find newly unlocked achievements that haven't been notified yet
      const newlyUnlockedAchievements = data.filter(achievement => 
        achievement.unlocked && !achievement.notified
      );
      
      // Use the centralized achievement handler which includes toast display and marking as notified
      if (newlyUnlockedAchievements.length > 0) {
        await handleNewAchievements(newlyUnlockedAchievements);
        
        // Update local state to reflect that achievements have been notified
        setAchievements(prev => 
          prev.map(achievement => 
            newlyUnlockedAchievements.some(newAch => newAch.id === achievement.id)
              ? { ...achievement, notified: true }
              : achievement
          )
        );
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      addToast({
        type: 'error',
        message: 'Failed to load achievements. Please refresh the page to try again.',
        autoClose: true,
        autoCloseDelay: TOAST_AUTO_CLOSE_DELAY
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Filter achievements based on selected filter
  const filterAchievements = (achievementList) => {
    switch (filter) {
      case 'unlocked':
        return achievementList.filter(a => a.unlocked);
      case 'locked':
        return achievementList.filter(a => !a.unlocked);
      case 'special':
        return achievementList.filter(a => a.category === 'user-groups');
      case 'daily-streak':
        return achievementList.filter(a => a.category === 'daily-streak');
      case 'total-wins':
        return achievementList.filter(a => gameTypes.includes(a.game_type));
      default:
        return achievementList;
    }
  };

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  if (loading || isLoading) {
    return (
      <div className="achievements-page">
        <div className="achievements-page__container">
          <div className="achievements-page__loading">Loading achievements...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'unlocked', label: 'Unlocked' },
    { value: 'locked', label: 'Locked' },
    { value: 'special', label: 'Special' },
    { value: 'daily-streak', label: 'Daily Streak' },
    { value: 'total-wins', label: 'Total Wins' },
  ];

  return (
    <div className="achievements-page">
      <div className="achievements-page__container">
        <div className="achievements-page__header">
          <h1>Achievements</h1>
          <p>Track your progress and unlock rewards as you play</p>
          
          <div className="achievements-page__stats">
            <div className="achievements-page__stat achievements-page__stat--single">
              <span className="achievements-page__stat-number">{unlockedCount}</span>
              <span className="achievements-page__stat-label">Achievements Unlocked</span>
            </div>
          </div>
        </div>

        <div className="achievements-page__filter-bar">
          {filterOptions.map(option => (
            <button
              key={option.value}
              className={`achievements-page__filter-button ${filter === option.value ? 'active' : ''}`}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="achievements-page__grid">
          {filterAchievements(achievements).map((achievement) => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
            />
          ))}

          {filterAchievements(achievements).length === 0 && (
            <div className="achievements-page__empty">
              <h3>No achievements found</h3>
              <p>Try a different filter or start playing games to unlock achievements!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AchievementsPage;